var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseAccountAPI } from '@account-abstraction/sdk/dist/src/BaseAccountAPI';
import { BigNumber } from 'ethers';
import { defaultAbiCoder, hexConcat } from 'ethers/lib/utils';
import { PassKeysAccount__factory, PassKeysAccountFactory__factory } from '@itsobvioustech/aa-passkeys-wallet/';
export class PassKeysAccountApi extends BaseAccountAPI {
    constructor(params) {
        var _a;
        super(params);
        this.factoryAddress = params.factoryAddress;
        this.index = (_a = params.index) !== null && _a !== void 0 ? _a : BigNumber.from(0);
        this.passKeyPair = params.passKeyPair;
        this.txnProgressCallback = params.txnProgressCallback;
    }
    setTxnProgressCallback(txnProgressCallback) {
        this.txnProgressCallback = txnProgressCallback;
    }
    _getAccountContract() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.accountContract) {
                this.accountContract = PassKeysAccount__factory.connect(yield this.getAccountAddress(), this.provider);
            }
            return this.accountContract;
        });
    }
    getAccountInitCode() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.factoryContract == null) {
                if (this.factoryAddress != null && this.factoryAddress !== '') {
                    this.factoryContract = PassKeysAccountFactory__factory.connect(this.factoryAddress, this.provider);
                }
                else {
                    throw new Error('factoryAddress is not set');
                }
            }
            if (this.passKeyPair.pubKeyX.isZero() || this.passKeyPair.pubKeyY.isZero()) {
                throw new Error('Cannot initialise with this passkey');
            }
            return hexConcat([
                this.factoryContract.address,
                this.factoryContract.interface.encodeFunctionData("createAccount", [
                    this.index,
                    this.passKeyPair.keyId,
                    this.passKeyPair.pubKeyX,
                    this.passKeyPair.pubKeyY
                ])
            ]);
        });
    }
    changePassKeyPair(passKeyPair) {
        this.passKeyPair = passKeyPair;
    }
    getNonce() {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this.checkAccountPhantom()) {
                return BigNumber.from(0);
            }
            const accountContract = yield this._getAccountContract();
            return yield accountContract.nonce();
        });
    }
    encodeExecute(target, value, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountContract = yield this._getAccountContract();
            return accountContract.interface.encodeFunctionData('execute', [
                target,
                value,
                data
            ]);
        });
    }
    signUserOpHash(userOpHash) {
        return __awaiter(this, void 0, void 0, function* () {
            let sig = yield this.passKeyPair.signChallenge(userOpHash);
            let encodedSig = defaultAbiCoder.encode(['bytes32', 'uint256', 'uint256', 'bytes', 'string', 'string'], [
                sig.id,
                sig.r,
                sig.s,
                sig.authData,
                sig.clientDataPrefix,
                sig.clientDataSuffix
            ]);
            return encodedSig;
        });
    }
    /**
     * return maximum gas used for verification.
     * NOTE: createUnsignedUserOp will add to this value the cost of creation, if the contract is not yet created.
     */
    getVerificationGasLimit() {
        return __awaiter(this, void 0, void 0, function* () {
            return 600000;
        });
    }
    /**
     * should cover cost of putting calldata on-chain, and some overhead.
     * actual overhead depends on the expected bundle size
     */
    getPreVerificationGas(userOp) {
        const _super = Object.create(null, {
            getPreVerificationGas: { get: () => super.getPreVerificationGas }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const estimate = yield _super.getPreVerificationGas.call(this, userOp);
            return estimate * 5;
        });
    }
    /**
     * Sign the filled userOp.
     * @param userOp the UserOperation to sign (with signature field ignored)
     */
    signUserOp(userOp) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.txnProgressCallback) === null || _a === void 0 ? void 0 : _a.call(this, userOp, 'pre_sign');
            const userOpHash = yield this.getUserOpHash(userOp);
            const signature = yield this.signUserOpHash(userOpHash);
            return Object.assign(Object.assign({}, userOp), { signature });
        });
    }
}
